import React from 'react'
import { useIntl } from 'react-intl'
import { css } from '@emotion/css'
import { Link, Text } from '@contrast-security-inc/design-system-typography'
import { Box, Row } from '../../../../legacy-design-system/form'
import { TextInput } from '../../../../legacy-design-system/form-text'
import { Label } from '../../../../legacy-design-system/form-label'
import { BasicTooltipContent } from '../../../../legacy-design-system/basic-tooltip-content'
import { useJiraIntegrationPage } from './jira-integration-page.provider'
import { useFormContext, Controller } from 'react-hook-form'
import {
  resetConnectionState,
  updateManagingCredentials
} from './state/jira-integration-page-actions'
import { JiraIntegrationForm } from './jira-form-schema'
import { JiraFormFieldTypes } from './jira-form-field-types'
import { useCredentialsQuery } from '../../credentials/api/use-credentials'
import { SingleSelectCombobox } from '../../../../legacy-design-system/combobox/single-select-combobox'
import { LatestUsedCredentials } from '../../credentials/api/shared'
import { updateAnyState } from './state/jira-integration-page.state'

const CredentialsInputConnectionName = ({
  control,
  formatMessage,
  hasPreviousCredentials,
  setValue
}) => {
  return (
    <Box basis={5}>
      <Controller
        control={control}
        name="name"
        render={({
          field: { value, onChange, ref, onBlur },
          fieldState: { error }
        }) => (
          <TextInput
            data-testid="jira-form-connection-name"
            value={value ?? ''}
            required={true}
            label={formatMessage({ id: 'title_connectionName' })}
            placeholder={formatMessage({
              id: 'placeholder_integrationsSettings_jiraName'
            })}
            onChange={(event) => {
              onChange(event)
              setValue('name', `${event.replace(/\s/g, '')}`)
              if (!hasPreviousCredentials) {
                setValue(
                  JiraFormFieldTypes.CREDENTIALS_NAME,
                  `${event.replace(/\s/g, '')}`
                )
              }
            }}
            onBlur={onBlur}
            inputRef={ref}
            errorMessage={error?.message}
          />
        )}
      />
    </Box>
  )
}

const CredentialsInputCredentialsName = ({
  control,
  formatMessage,
  isManagingCredentials,
  enableManagingCredentials,
  setValue
}) => {
  return (
    <Box
      basis={5}
      className={css`
        position: relative;
      `}
    >
      {!isManagingCredentials && (
        <Link
          testId="jira-form-manage-credentials-link"
          className={css`
            position: absolute;
            right: 0;
          `}
          onClick={enableManagingCredentials}
        >
          <Text font="metadata">
            {formatMessage({
              id: 'action_integrationsSettings_manageCredentials'
            })}
          </Text>
        </Link>
      )}
      <Controller
        control={control}
        name={JiraFormFieldTypes.CREDENTIALS_NAME}
        render={({
          field: { value, onChange, ref, onBlur },
          fieldState: { error }
        }) => (
          <TextInput
            data-testid="jira-form-credentials-name"
            value={value ?? ''}
            required={true}
            label={formatMessage({ id: 'title_credentialsName' })}
            disabled={!isManagingCredentials}
            placeholder={formatMessage({
              id: 'placeholder_integrationsSettings_jiraCredentialName'
            })}
            onChange={(event) => {
              onChange(event)
              setValue(
                JiraFormFieldTypes.CREDENTIALS_NAME,
                `${event.replace(/\s/g, '')}`
              )
            }}
            onBlur={onBlur}
            inputRef={ref}
            errorMessage={error?.message}
          />
        )}
      />
    </Box>
  )
}

const CredentialsDropdown = ({
  control,
  formatMessage,
  isManagingCredentials,
  enableManagingCredentials,
  setValue,
  credentialsList,
  dispatch
}) => {
  return (
    <Box
      basis={5}
      className={css`
        position: relative;
      `}
    >
      {!isManagingCredentials && (
        <Link
          testId="jira-form-manage-credentials-link"
          className={css`
            position: absolute;
            right: 0;
            z-index: 1;
          `}
          onClick={enableManagingCredentials}
        >
          <Text font="metadata">
            {formatMessage({
              id: 'action_integrationsSettings_manageCredentials'
            })}
          </Text>
        </Link>
      )}
      <Controller
        control={control}
        name={JiraFormFieldTypes.CREDENTIALS}
        render={({ field: { value, onChange } }) => (
          <SingleSelectCombobox<LatestUsedCredentials>
            data-testid="jira-form-credentials-name-dropdown"
            value={
              //  Only load the UI once the full data structure has been found & set, not just the string ID on initial load
              typeof value === 'object' ? value : undefined
            }
            label={formatMessage({ id: 'title_credentialsName' })}
            onChange={(event) => {
              onChange(event)
              setValue(
                JiraFormFieldTypes.CREDENTIALS_NAME,
                `${event.name.replace(/\s/g, '')}`
              )
              setValue(JiraFormFieldTypes.CREDENTIALS_ID, event.id)
              setValue(JiraFormFieldTypes.PROJECT, '')
              setValue(JiraFormFieldTypes.ISSUE_TYPE, '')
              setValue(JiraFormFieldTypes.PROJECT_FIELDS, [])
              dispatch(
                updateAnyState({
                  hasCredentialsUpdate: true
                })
              )
            }}
            items={[{ items: credentialsList }]}
            itemToString={(item) => `${item.host} - ${item.name}`}
          />
        )}
      />
    </Box>
  )
}

const CredentialsInputUrl = ({
  isManagingCredentials,
  enableManagingCredentials,
  formatMessage,
  hasPreviousCredentials,
  control,
  resetCredentialsState
}) => {
  return (
    <Box
      basis={5}
      className={css`
        position: relative;
      `}
    >
      {hasPreviousCredentials && !isManagingCredentials && (
        <Link
          className={css`
            position: absolute;
            right: 0;
          `}
          onClick={enableManagingCredentials}
        >
          <Text font="metadata">
            {formatMessage({
              id: 'action_integrationsSettings_manageCredentials'
            })}
          </Text>
        </Link>
      )}
      <Controller
        control={control}
        name={JiraFormFieldTypes.CREDENTIALS_HOST}
        render={({
          field: { onChange, ref, value },
          fieldState: { error }
        }) => (
          <TextInput
            data-testid="jira-form-credentials-url"
            label={formatMessage({ id: 'title_url' })}
            required={true}
            disabled={hasPreviousCredentials && !isManagingCredentials}
            placeholder={formatMessage(
              {
                id: 'variable_integrationsSettings_placeholderURL_react'
              },
              { url: 'https://jira.hostname.com/' }
            )}
            value={value ?? ''}
            onChange={(event) => {
              onChange(event)
              resetCredentialsState()
            }}
            inputRef={ref}
            errorMessage={error?.message}
          />
        )}
      />
    </Box>
  )
}

export const CredentialsFields = () => {
  const { state: pageState, dispatch } = useJiraIntegrationPage()
  const { isManagingCredentials, organizationUuid } = pageState

  const formContext = useFormContext<JiraIntegrationForm>()
  const { control, setValue, getValues } = formContext
  const hasPreviousCredentials = !!getValues(JiraFormFieldTypes.CREDENTIALS_ID)

  const { formatMessage } = useIntl()

  const resetCredentialsState = () =>
    resetConnectionState({ dispatch, pageState })

  const enableManagingCredentials = () =>
    updateManagingCredentials({
      dispatch,
      formContext,
      pageState,
      isManagingCredentials: true
    })

  const { credentialsList } = useCredentialsQuery({
    bugtracker: 'JIRA',
    organizationUuid
  })

  return (
    <Row>
      {!hasPreviousCredentials && (
        <>
          <CredentialsInputConnectionName
            control={control}
            formatMessage={formatMessage}
            hasPreviousCredentials={hasPreviousCredentials}
            setValue={setValue}
          />
          <CredentialsInputUrl
            isManagingCredentials={isManagingCredentials}
            enableManagingCredentials={enableManagingCredentials}
            formatMessage={formatMessage}
            control={control}
            hasPreviousCredentials={hasPreviousCredentials}
            resetCredentialsState={resetCredentialsState}
          />
        </>
      )}
      {hasPreviousCredentials && !isManagingCredentials && (
        <>
          <CredentialsInputConnectionName
            control={control}
            formatMessage={formatMessage}
            hasPreviousCredentials={hasPreviousCredentials}
            setValue={setValue}
          />
          <CredentialsDropdown
            credentialsList={credentialsList}
            control={control}
            formatMessage={formatMessage}
            setValue={setValue}
            isManagingCredentials={isManagingCredentials}
            enableManagingCredentials={enableManagingCredentials}
            dispatch={dispatch}
          />
        </>
      )}

      {hasPreviousCredentials && isManagingCredentials && (
        <>
          <CredentialsInputCredentialsName
            isManagingCredentials={isManagingCredentials}
            enableManagingCredentials={enableManagingCredentials}
            control={control}
            formatMessage={formatMessage}
            setValue={setValue}
          />
          <CredentialsInputUrl
            isManagingCredentials={isManagingCredentials}
            enableManagingCredentials={enableManagingCredentials}
            formatMessage={formatMessage}
            hasPreviousCredentials={hasPreviousCredentials}
            control={control}
            resetCredentialsState={resetCredentialsState}
          />
        </>
      )}
      {/*
      If there are no previous credentials returned from the backend
      the username and api key input fields should be visible.
      */}
      {(isManagingCredentials || !hasPreviousCredentials) && (
        <>
          <Box basis={5}>
            <Controller
              control={control}
              name={JiraFormFieldTypes.CREDENTIALS_USERNAME}
              rules={{ required: true }}
              render={({
                field: { onChange, ref, value },
                fieldState: { error }
              }) => (
                <TextInput
                  data-testid="jira-form-credentials-username"
                  value={value ?? ''}
                  required={true}
                  label={formatMessage({ id: 'title_username' })}
                  onChange={(event) => {
                    onChange(event)
                    resetCredentialsState()
                  }}
                  inputRef={ref}
                  errorMessage={error?.message}
                />
              )}
            />
          </Box>
          <Box basis={5}>
            <Controller
              control={control}
              name={JiraFormFieldTypes.CREDENTIALS_PASSWORD}
              rules={{ required: true }}
              render={({
                field: { onChange, ref, value },
                fieldState: { error }
              }) => (
                <TextInput
                  data-testid="jira-form-credentials-password"
                  value={value ?? ''}
                  required={true}
                  label={formatMessage({ id: 'title_apiKey' })}
                  renderLabel={() => (
                    <Label
                      children={formatMessage({
                        id: 'title_apiKey'
                      })}
                      renderHelpContent={() => (
                        <BasicTooltipContent
                          i18nTitle="title_apiKey"
                          i18nDescription="detail_integrationsSettings_jiraApiKey"
                          i18nLinkText="action_getStarted"
                          linkUrl="https://id.atlassian.com/manage/api-tokens"
                        />
                      )}
                    />
                  )}
                  onChange={(event) => {
                    onChange(event)
                    resetCredentialsState()
                  }}
                  inputRef={ref}
                  errorMessage={error?.message}
                  type="password"
                />
              )}
            />
          </Box>
        </>
      )}
    </Row>
  )
}
